import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react"

const REVIEWS_PER_PAGE = 10
/** Start fetching well before the sentinel reaches the viewport bottom. */
const PREFETCH_ROOT_MARGIN = "0px 0px 800px 0px"
const NEAR_BOTTOM_PX = 160
const SCROLL_EASE_MS = 360

type ReviewRecord = Record<string, unknown> & { id?: number | string }

type ScrollSnapshot = {
  scrollY: number
  scrollHeight: number
  wasNearBottom: boolean
}

type UseReviewsInfiniteScrollResult = {
  allReviews: ReviewRecord[]
  isLoading: boolean
  hasMoreReviews: boolean
  sentinelRef: RefObject<HTMLDivElement | null>
}

export function useReviewsInfiniteScroll(
  practiceId: number | string | undefined,
  initialReviews: ReviewRecord[] = [],
  enabled = true
): UseReviewsInfiniteScrollResult {
  const [allReviews, setAllReviews] = useState(initialReviews)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMoreReviews, setHasMoreReviews] = useState(true)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollSnapshotRef = useRef<ScrollSnapshot | null>(null)
  const easingFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (initialReviews.length > 0 && allReviews.length === 0) {
      setAllReviews(initialReviews)
    }
  }, [initialReviews, allReviews.length])

  const loadMoreReviews = useCallback(async () => {
    if (!enabled || isLoading || !hasMoreReviews || !practiceId) return

    const scrollY = window.scrollY
    const scrollHeight = document.documentElement.scrollHeight
    const maxScroll = Math.max(0, scrollHeight - window.innerHeight)

    scrollSnapshotRef.current = {
      scrollY,
      scrollHeight,
      wasNearBottom: scrollY >= maxScroll - NEAR_BOTTOM_PX
    }

    setIsLoading(true)
    try {
      const response = await fetch(
        `/practices/${practiceId}/reviews?page=${currentPage + 1}&per_page=${REVIEWS_PER_PAGE}`
      )
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      if (data.reviews?.length > 0) {
        setAllReviews((prev) => [...prev, ...data.reviews])
        setCurrentPage((prev) => prev + 1)
        if (data.reviews.length < REVIEWS_PER_PAGE) setHasMoreReviews(false)
      } else {
        setHasMoreReviews(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error loading more reviews:", error)
      scrollSnapshotRef.current = null
    } finally {
      setIsLoading(false)
    }
  }, [enabled, practiceId, currentPage, hasMoreReviews, isLoading])

  useEffect(() => {
    if (!enabled) return undefined

    const sentinel = sentinelRef.current
    if (!sentinel) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreReviews()
      },
      { root: null, rootMargin: PREFETCH_ROOT_MARGIN, threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [enabled, loadMoreReviews])

  useLayoutEffect(() => {
    const snapshot = scrollSnapshotRef.current
    if (!snapshot) return

    scrollSnapshotRef.current = null
    const heightAdded = document.documentElement.scrollHeight - snapshot.scrollHeight
    if (heightAdded <= 0) return

    if (easingFrameRef.current) cancelAnimationFrame(easingFrameRef.current)

    if (snapshot.wasNearBottom) {
      const startY = window.scrollY
      const targetY = snapshot.scrollY + heightAdded
      const startTime = performance.now()

      const step = (now: number) => {
        const progress = Math.min((now - startTime) / SCROLL_EASE_MS, 1)
        const eased = 1 - (1 - progress) ** 3
        window.scrollTo(0, startY + (targetY - startY) * eased)
        if (progress < 1) {
          easingFrameRef.current = requestAnimationFrame(step)
        } else {
          easingFrameRef.current = null
        }
      }

      easingFrameRef.current = requestAnimationFrame(step)
    } else {
      window.scrollTo(0, snapshot.scrollY)
    }
  }, [allReviews.length])

  useEffect(
    () => () => {
      if (easingFrameRef.current) cancelAnimationFrame(easingFrameRef.current)
    },
    []
  )

  return { allReviews, isLoading, hasMoreReviews, sentinelRef }
}
